import { isInTable, selectionCell } from "@tiptap/pm/tables";
import { TextSelection } from "@tiptap/pm/state";
import { findTable } from "prosemirror-utils";

import TableStepper from "./table-stepper";

export default class TableUtils {
    editor;
    state;
    doc;
    tr;
    resolvedCelPos;
    table;
    tableJSON;
    tableCell;
    canSplit = true;
    tablePos;
    tableStart;
    tableSize;
    tableRow;
    splitCellRow;
    splitCellPosition;
    maxTotalWidth = 650;
    effectiveMaxWidth = 650; // This will be calculated based on context

    constructor(api) {
        const { editor, state, tr } = api;

        this.editor = editor;
        this.state = state;
        this.doc = editor.state.doc;
        this.tr = tr;

        this.init();
    }

    init() {
        if (!isInTable(this.state)) {
            this.canSplit = false;
            return;
        }

        this.resolvedCelPos = selectionCell(this.state);

        if (!this.resolvedCelPos) {
            this.canSplit = false;
            return;
        }

        const { pos, start, node } = findTable(this.state.selection) || {};
        if (!node) {
            this.canSplit = false;
            return;
        }

        this.originalTableNode = node;
        this.table = node.copy(node.content);
        this.tablePos = pos;
        this.tableStart = start;
        this.tableSize = node.nodeSize;

        // Calculate effective max width based on parent context
        this.effectiveMaxWidth = this._calculateEffectiveMaxWidth();

        const { cellNode, rowNode, cellIndex, rowIndex } = this._findCellAndRowInfo();
        if (!cellNode || !rowNode) {
            this.canSplit = false;
            return;
        }

        this.tableCell = cellNode;
        this.tableRow = rowNode;
        this.splitCellRow = rowIndex;
        this.splitCellPosition = cellIndex;

        this._copyValues();

        this.tableJSON = this.table.toJSON();
    }

    /**
     * Calculate the effective maximum width for the table based on its context
     * If the table is nested inside another table cell, use the parent cell's width
     * Otherwise, use the default maxTotalWidth
     */
    _calculateEffectiveMaxWidth() {
        try {
            // Check if this table is inside another table cell
            const $tablePos = this.state.doc.resolve(this.tablePos);

            // Walk up the document tree to find if we're inside a table cell
            for (let depth = $tablePos.depth; depth >= 0; depth--) {
                const node = $tablePos.node(depth);

                if (node && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
                    // Try to get the parent cell's width from its colwidth attribute
                    if (node.attrs && node.attrs.colwidth && node.attrs.colwidth[0]) {
                        const parentCellWidth = node.attrs.colwidth.reduce(
                            (sum, width) => sum + width,
                            0,
                        );
                        const padding = 20; // Account for cell padding
                        const effectiveWidth = Math.max(100, parentCellWidth - padding);
                        return effectiveWidth;
                    }

                    // Fallback: try to find the parent table and calculate from its width
                    const parentTable = this._findParentTable($tablePos, depth);
                    if (parentTable) {
                        const parentTableWidth = this._getTableTotalWidth(parentTable);
                        // Estimate based on parent table width divided by columns
                        const parentColumnCount = this._getTableColumnCount(parentTable);
                        const estimatedCellWidth = Math.floor(parentTableWidth / parentColumnCount);
                        const padding = 20;
                        const effectiveWidth = Math.max(100, estimatedCellWidth - padding);
                        return effectiveWidth;
                    }

                    // Conservative fallback for nested tables
                    return 400;
                }
            }

            // Also check if we can detect nesting through DOM (as a fallback)
            if (this.editor.view) {
                try {
                    const domAtPos = this.editor.view.domAtPos(this.tablePos);
                    if (domAtPos && domAtPos.node) {
                        let currentNode = domAtPos.node;

                        // Find the TABLE element
                        while (currentNode && currentNode.nodeName !== "TABLE") {
                            currentNode = currentNode.parentNode;
                        }

                        if (currentNode && currentNode.nodeName === "TABLE") {
                            // Check if this table is inside a TD or TH
                            let parentNode = currentNode.parentNode;
                            while (parentNode) {
                                if (parentNode.nodeName === "TD" || parentNode.nodeName === "TH") {
                                    // Found parent cell via DOM
                                    const cellWidth = parentNode.offsetWidth;
                                    const computedStyle = window.getComputedStyle(parentNode);
                                    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                                    const paddingRight =
                                        parseFloat(computedStyle.paddingRight) || 0;
                                    const effectiveWidth = Math.max(
                                        100,
                                        cellWidth - paddingLeft - paddingRight,
                                    );
                                    return effectiveWidth;
                                }

                                if (parentNode.nodeName === "TABLE") {
                                    break; // Stop at the next table level
                                }

                                parentNode = parentNode.parentNode;
                            }
                        }
                    }
                } catch (e) {
                    console.warn("Error checking DOM for parent cell:", e);
                }
            }

            // Not nested, use default
            return this.maxTotalWidth;
        } catch (e) {
            return this.maxTotalWidth;
        }
    }

    /**
     * Find the parent table node from a given position
     */
    _findParentTable($pos, startDepth) {
        for (let depth = startDepth - 1; depth >= 0; depth--) {
            const node = $pos.node(depth);
            if (node && node.type.name === "table") {
                return node;
            }
        }
        return null;
    }

    /**
     * Get the total width of a table from its column widths
     */
    _getTableTotalWidth(tableNode) {
        if (!tableNode || !tableNode.content || !tableNode.content.content[0]) {
            return this.maxTotalWidth;
        }

        const firstRow = tableNode.content.content[0];
        let totalWidth = 0;

        firstRow.content.content.forEach((cell) => {
            if (cell.attrs.colwidth) {
                totalWidth += cell.attrs.colwidth.reduce((sum, width) => sum + width, 0);
            } else {
                // Estimate if no colwidth
                totalWidth += 100;
            }
        });

        return totalWidth || this.maxTotalWidth;
    }

    /**
     * Get the number of columns in a table
     */
    _getTableColumnCount(tableNode) {
        if (!tableNode || !tableNode.content || !tableNode.content.content[0]) {
            return 1;
        }

        const firstRow = tableNode.content.content[0];
        return firstRow.content.content.reduce((acc, cell) => acc + (cell.attrs.colspan || 1), 0);
    }

    /*
     ** Copies the values from the tableNode to a new table node to not affect the original table node.
     */
    _copyValues() {
        const tableJSON = JSON.parse(JSON.stringify(this.table.toJSON()));
        this.table = this.editor.schema.nodeFromJSON(tableJSON);
        this.tableRow = this.table.content.content[this.splitCellRow];
        this.tableCell = this.tableRow?.content?.content?.[this.splitCellPosition];

        if (!this.tableCell) {
            this.canSplit = false;
        }
    }

    _findCell() {
        let cellNode;

        this.state.doc.descendants((node, pos) => {
            if (
                ["tableHeader", "tableCell"].includes(node.type.name) &&
                this.state.selection.from >= pos &&
                this.state.selection.to <= pos + node.nodeSize
            ) {
                cellNode = node;
                return false; // Stop the traversal when the node is found
            }
        });

        if (!cellNode) {
            this.canSplit = false;
        }

        return cellNode;
    }

    /**
     * Get all nested table ranges within the target table
     * Returns an array of {start, end} objects for each nested table
     */
    _getNestedTableRanges() {
        const tableEnd = this.tablePos + this.tableSize;
        const nestedRanges = [];

        this.state.doc.descendants((node, pos) => {
            // Stop if we've passed our table
            if (pos >= tableEnd) {
                return false;
            }

            // Skip positions before our table
            if (pos < this.tablePos) {
                return true;
            }

            // Found a nested table (not our target table)
            if (node.type.name === "table" && pos !== this.tablePos) {
                nestedRanges.push({
                    start: pos,
                    end: pos + node.nodeSize,
                });
                return false; // Don't descend into nested tables
            }

            return true;
        });

        return nestedRanges;
    }

    /**
     * Check if a position is inside any of the nested table ranges
     */
    _isInsideNestedTableRange(pos, nestedRanges) {
        return nestedRanges.some((range) => pos >= range.start && pos < range.end);
    }

    _findCellAndRowInfo() {
        let cellNode;
        let rowNode;
        let rowIndex = -1;
        let cellIndex = -1;

        // We need to find the cell within THIS specific table, not any table
        const tableEnd = this.tablePos + this.tableSize;

        // Get all nested table ranges upfront
        const nestedTableRanges = this._getNestedTableRanges();

        // Track our position as we traverse to find the selected cell
        let inTargetTable = false;
        let currentRowIndex = -1;
        let cellPositionsInRow = [];

        this.state.doc.descendants((node, pos) => {
            // Track when we enter/exit our target table
            if (node.type.name === "table") {
                if (pos === this.tablePos) {
                    inTargetTable = true;
                    currentRowIndex = -1;
                    cellPositionsInRow = [];
                } else if (pos >= tableEnd) {
                    inTargetTable = false;
                    return false; // Stop if we've passed our table
                } else if (
                    inTargetTable &&
                    this._isInsideNestedTableRange(pos, nestedTableRanges)
                ) {
                    // This is a nested table, skip it and its contents
                    return false;
                }
            }

            // Check if current position is inside a nested table
            const isInsideNestedTable = this._isInsideNestedTableRange(pos, nestedTableRanges);

            // Track rows within our target table (but not nested tables)
            if (inTargetTable && !isInsideNestedTable && node.type.name === "tableRow") {
                currentRowIndex++;
                cellPositionsInRow = []; // Reset cell positions for new row
            }

            // Track cells within current row of target table (but not nested tables)
            if (
                inTargetTable &&
                !isInsideNestedTable &&
                ["tableHeader", "tableCell"].includes(node.type.name)
            ) {
                cellPositionsInRow.push(pos);

                // Check if this is the selected cell (not inside a nested table)
                if (
                    this.state.selection.from >= pos &&
                    this.state.selection.to <= pos + node.nodeSize
                ) {
                    // Check if selection is inside a nested table within this cell
                    const isSelectionInNestedTable = this._isSelectionInNestedTable(node, pos);

                    // If selection is inside a nested table, skip this cell
                    if (!isSelectionInNestedTable) {
                        cellNode = node;
                        rowIndex = currentRowIndex;
                        cellIndex = cellPositionsInRow.length - 1;
                    }
                }
            }
        });

        if (!cellNode || rowIndex < 0 || cellIndex < 0) {
            this.canSplit = false;
            return {};
        }

        // Get the row node from our table copy
        rowNode = this.table.content.content[rowIndex];
        if (!rowNode) {
            this.canSplit = false;
            return {};
        }

        // Verify the cell index is valid
        if (cellIndex >= rowNode.content.content.length) {
            this.canSplit = false;
            return {};
        }

        return { cellNode, rowNode, cellIndex, rowIndex };
    }

    /**
     * Check if the current selection is inside a nested table within a cell
     */
    _isSelectionInNestedTable(cellNode, cellPos) {
        const selectionFrom = this.state.selection.from;
        const selectionTo = this.state.selection.to;

        // Look for nested tables in the cell
        let isInNested = false;

        this.state.doc.nodesBetween(cellPos, cellPos + cellNode.nodeSize, (node, pos) => {
            if (node.type.name === "table" && pos > cellPos) {
                // This is a nested table
                const tableEnd = pos + node.nodeSize;
                if (selectionFrom >= pos && selectionTo <= tableEnd) {
                    isInNested = true;
                    return false; // Stop traversal
                }
            }
        });

        return isInNested;
    }

    insertCellsInRow(row, cells, index) {
        row.content.splice(index, 0, ...cells);
    }

    _cancel() {
        return { tr: this.tr, result: false };
    }

    _fixColumnWidths() {
        const columnCount = this.tableJSON.content[0].content.reduce(
            (acc, cell) => acc + cell.attrs.colspan,
            0,
        );

        // Use the effective max width instead of the hardcoded value
        const maxColumnWidth = Math.floor(this.effectiveMaxWidth / columnCount);

        this.tableJSON.content.forEach((row) => {
            row.content.forEach((cell) => {
                const colspan = cell.attrs.colspan || 1;
                cell.attrs.colwidth = new Array(colspan).fill(maxColumnWidth);
            });
        });
    }

    _executeSplit() {
        this._fixColumnWidths();
        const newTable = this.editor.schema.nodeFromJSON(this.tableJSON);

        // Store the original selection before replacing the table
        const originalSelection = this.state.selection;
        const originalFrom = originalSelection.from;

        // Replace the table
        this.tr = this.tr.replaceWith(this.tablePos, this.tablePos + this.tableSize, newTable);

        // Calculate the new selection position
        // The mapping should preserve the relative position within the table
        const mappedPos = this.tr.mapping.map(originalFrom);

        // Try to find a valid position near the mapped position
        let newPos = mappedPos;

        // If we have information about the split cell, try to position cursor in or near it
        if (this.splitCellRow !== undefined && this.splitCellPosition !== undefined) {
            try {
                // Try to calculate position based on the split cell's location
                const $tablePos = this.tr.doc.resolve(this.tablePos);
                if ($tablePos.nodeAfter && $tablePos.nodeAfter.type.name === "table") {
                    // Navigate to the approximate position of the split cell
                    let pos = this.tablePos + 1; // Start inside the table

                    // Skip to the target row
                    const tableNode = $tablePos.nodeAfter;
                    for (
                        let row = 0;
                        row < this.splitCellRow && row < tableNode.content.childCount;
                        row++
                    ) {
                        pos += tableNode.content.child(row).nodeSize;
                    }

                    // Now we're at the target row, skip to the target cell
                    if (this.splitCellRow < tableNode.content.childCount) {
                        const targetRow = tableNode.content.child(this.splitCellRow);
                        pos += 1; // Enter the row

                        for (
                            let cell = 0;
                            cell < this.splitCellPosition && cell < targetRow.content.childCount;
                            cell++
                        ) {
                            pos += targetRow.content.child(cell).nodeSize;
                        }

                        // Position cursor inside the cell
                        if (this.splitCellPosition < targetRow.content.childCount) {
                            pos += 1; // Enter the cell
                            newPos = pos;
                        }
                    }
                }
            } catch (e) {
                // Fall back to mapped position
                newPos = mappedPos;
            }
        }

        // Ensure the position is valid
        try {
            // const $newPos = this.tr.doc.resolve(newPos);
            this.tr = this.tr.setSelection(TextSelection.create(this.tr.doc, newPos));
        } catch (e) {
            // Try to find a safe position within the table
            const safePos = Math.min(
                Math.max(this.tablePos + 1, mappedPos),
                this.tablePos + newTable.nodeSize - 1,
            );
            try {
                this.tr = this.tr.setSelection(TextSelection.create(this.tr.doc, safePos));
            } catch (e2) {
                // Last resort: just use the original mapped position
                this.tr = this.tr.setSelection(
                    TextSelection.create(this.tr.doc, this.tablePos + 1),
                );
            }
        }

        return { tr: this.tr, result: true };
    }

    splitCellVertically(numberOfCells = 2) {
        if (!this.canSplit) {
            return this._cancel();
        }

        const { rowspan, colspan } = this.tableCell?.attrs || { rowspan: 1, colspan: 1 };

        // First check - the cell spans over multiple rows so before doing anything else just split this cell.
        if (colspan > 1) {
            // Get spans of new (inserted) cells and span to update of split cell.
            const { newCellsSpan, updatedSpan } = this.breakSpanEvenly(colspan, numberOfCells);

            this.tableCell.attrs.colspan = updatedSpan;

            // Each inserted cell will have the same attributes:
            const newCellsAttributes = { colspan: 1, rowspan: 1 };

            // Do not store default value in the model.
            if (newCellsSpan > 1) {
                newCellsAttributes.colspan = newCellsSpan;
            }

            // Copy rowspan of split cell.
            if (rowspan > 1) {
                newCellsAttributes.rowspan = rowspan;
            }

            const cellsToInsert = colspan > numberOfCells ? numberOfCells - 1 : colspan - 1;

            // insert in the same row after the cell we are splitting
            const cells = this.createCells(
                cellsToInsert,
                this.tableCell.type.name,
                newCellsAttributes,
            );

            this.insertCellsInRow(
                this.tableJSON.content[this.splitCellRow],
                cells,
                this.splitCellPosition + 1,
            );
        }

        // Second check - the cell has colspan of 1 or we need to create more cells then the currently one spans over.
        if (colspan < numberOfCells) {
            const cellsToInsert = numberOfCells - colspan;

            // First step: expand cells on the same column as split cell.
            const tableMap = [...new TableStepper(this.table)];

            // Get the column index of split cell.
            const { column: splitCellColumn } = tableMap.find(
                ({ cell }) => cell === this.tableCell,
            );

            // Find cells which needs to be expanded vertically - those on the same column or those that spans over split cell's column.
            const cellsToUpdate = tableMap.filter(({ cell, cellWidth, column }) => {
                const isOnSameColumn = cell !== this.tableCell && column === splitCellColumn;
                const spansOverColumn =
                    column < splitCellColumn && column + cellWidth > splitCellColumn;

                return isOnSameColumn || spansOverColumn;
            });

            // Expand cells vertically.
            for (const { cell, cellWidth } of cellsToUpdate) {
                cell.attrs.colspan = cellWidth + cellsToInsert;
            }

            // Second step: create columns after split cell.

            // Each inserted cell will have the same attributes:
            const newCellsAttributes = { colspan: 1, rowspan: 1 };

            // Copy rowspan of split cell.
            if (rowspan > 1) {
                newCellsAttributes.rowspan = rowspan;
            }

            // insert in the same row after the cell we are splitting
            const cells = this.createCells(
                cellsToInsert,
                this.tableCell.type.name,
                newCellsAttributes,
            );

            this.insertCellsInRow(
                this.tableJSON.content[this.splitCellRow],
                cells,
                this.splitCellPosition + 1,
            );
        }

        return this._executeSplit();
    }

    splitCellHorizontally(numberOfCells = 2) {
        if (!this.canSplit) {
            return this._cancel();
        }

        const { rowspan, colspan } = this.tableCell?.attrs || { rowspan: 1, colspan: 1 };

        // First check - the cell spans over multiple rows so before doing anything else just split this cell.
        if (rowspan > 1) {
            // Cache table map before updating table.
            const tableMap = [
                ...new TableStepper(this.table, {
                    startRow: this.splitCellRow,
                    endRow: this.splitCellRow + rowspan - 1,
                    includeAllSlots: true,
                }),
            ];

            // Get spans of new (inserted) cells and span to update of split cell.
            const { newCellsSpan, updatedSpan } = this.breakSpanEvenly(rowspan, numberOfCells);

            this.tableCell.attrs.rowspan = updatedSpan;

            const { column: cellColumn } = tableMap.find(({ cell }) => cell === this.tableCell);

            // Each inserted cell will have the same attributes:
            const newCellsAttributes = {};

            // Do not store default value in the model.
            if (newCellsSpan > 1) {
                newCellsAttributes.rowspan = newCellsSpan;
            }

            // Copy colspan of split cell.
            if (colspan > 1) {
                newCellsAttributes.colspan = colspan;
            }

            for (const tableSlot of tableMap) {
                const { column, row, cell } = tableSlot;

                // As both newly created cells and the split cell might have rowspan,
                // the insertion of new cells must go to appropriate rows:
                //
                // 1. It's a row after split cell + it's height.
                const isAfterSplitCell = row >= this.splitCellRow + updatedSpan;
                // 2. Is on the same column.
                const isOnSameColumn = column === cellColumn;
                // 3. And it's row index is after previous cell height.
                const isInEvenlySplitRow =
                    (row + this.splitCellRow + updatedSpan) % newCellsSpan === 0;

                if (isAfterSplitCell && isOnSameColumn && isInEvenlySplitRow) {
                    const cells = this.createCells(1, cell.type.name, newCellsAttributes);

                    this.tableJSON.content[row].content.splice(column, 0, cells[0]);
                }
            }
        }

        // Second check - the cell has rowspan of 1 or we need to create more cells than the current cell spans over.
        if (rowspan < numberOfCells) {
            // We already split the cell in check one so here we split to the remaining number of cells only.
            const cellsToInsert = numberOfCells - rowspan;

            // This check is needed since we need to check if there are any cells from previous rows than spans over this cell's row.
            const tableMap = [
                ...new TableStepper(this.table, { startRow: 0, endRow: this.splitCellRow }),
            ];

            // First step: expand cells.
            for (const { cell, cellHeight, row } of tableMap) {
                // Expand rowspan of cells that are either:
                // - on the same row as current cell,
                // - or are below split cell row and overlaps that row.
                if (cell !== this.tableCell && row + cellHeight > this.splitCellRow) {
                    const rowspanToSet = cellHeight + cellsToInsert;

                    cell.attrs.rowspan = rowspanToSet;
                }
            }

            // Second step: create rows with single cell below split cell.
            const newCellsAttributes = {};

            // Copy colspan of split cell.
            if (colspan > 1) {
                newCellsAttributes.colspan = colspan;
            }

            this.createEmptyRows(
                this.splitCellRow + 1,
                cellsToInsert,
                1,
                this.tableCell.type.name,
                newCellsAttributes,
            );
        }

        return this._executeSplit();
    }

    /**
     * Creates an empty table cell in the JSON format
     *
     * @param type The type of cell to create. tableHeader or tableCell.
     * @param attributes The attributes of the cells to create (colspan/rowspan).
     * @returns {Object} An empty table cell.
     */
    createEmptyTableCell(type = "tableCell", attrs = {}) {
        return {
            type,
            attrs,
            content: [{ type: "paragraph", attrs: { textAlign: "justify" } }],
        };
    }

    /**
     * Creates given number of empty cells.
     *
     * @param cellsCount The number of cells to create
     * @param type The type of cell to create. tableHeader or tableCell.
     * @param attributes The attributes of the cells to create (colspan/rowspan).
     * @returns {Array<Object>} An array of empty cells.
     */
    createCells(cellCount, type, attributes = {}) {
        const cells = [];

        for (let i = 0; i < cellCount; i++) {
            cells.push(this.createEmptyTableCell(type, attributes));
        }

        return cells;
    }

    /**
     * Creates empty rows at the given index in an existing table.
     *
     * @param insertAt The row index of row insertion.
     * @param rows The number of rows to create.
     * @param tableCellsToInsert The number of cells to insert in each row.
     * @param type The type of cell to create. tableHeader or tableCell.
     * @param attributes The attributes of the cells to create (colspan/rowspan).
     */
    createEmptyRows(insertAt, rows, tableCellsToInsert, type, attributes = {}) {
        const content = this.createCells(tableCellsToInsert, type, attributes);

        for (let i = 0; i < rows; i++) {
            const tableRow = {
                type: "tableRow",
                content,
            };

            this.tableJSON.content.splice(insertAt, 0, tableRow);
        }
    }

    /**
     * Gets the current column widths from the table.
     * Returns an array of widths for each column, accounting for colspan.
     */
    _getColumnWidths() {
        const firstRow = this.tableJSON.content[0];
        if (!firstRow) {
            return [];
        }

        const widths = [];
        for (const cell of firstRow.content) {
            const colspan = cell.attrs?.colspan || 1;
            const colwidth = cell.attrs?.colwidth;

            for (let i = 0; i < colspan; i++) {
                if (colwidth && colwidth[i]) {
                    widths.push(colwidth[i]);
                } else {
                    // Default width if not set
                    widths.push(null);
                }
            }
        }

        return widths;
    }

    /**
     * Calculates the total width of the table from column widths.
     * If columns don't have widths set, uses the effective max width.
     */
    _getTotalTableWidth() {
        const widths = this._getColumnWidths();
        const definedWidths = widths.filter((w) => w !== null);

        if (definedWidths.length === 0) {
            return this.effectiveMaxWidth;
        }

        // If some columns have widths, calculate total from them
        if (definedWidths.length === widths.length) {
            return definedWidths.reduce((sum, w) => sum + w, 0);
        }

        // Mixed case: some have widths, some don't - use effective max
        return this.effectiveMaxWidth;
    }

    /**
     * Redistributes column widths to accommodate a new column while
     * maintaining the total table width. This prevents the table from
     * expanding beyond its container.
     */
    _redistributeColumnWidths() {
        // Get the current column count AFTER insertion (tableJSON already has the new column)
        const firstRow = this.tableJSON.content[0];
        if (!firstRow) {
            return;
        }

        let newColumnCount = 0;
        for (const cell of firstRow.content) {
            newColumnCount += cell.attrs?.colspan || 1;
        }

        // Calculate the new width for each column
        const newColWidth = Math.floor(this.effectiveMaxWidth / newColumnCount);

        // Apply the new width to all cells in the table
        this.tableJSON.content.forEach((row) => {
            row.content.forEach((cell) => {
                const colspan = cell.attrs?.colspan || 1;
                cell.attrs.colwidth = new Array(colspan).fill(newColWidth);
            });
        });
    }

    insertTableColumn(direction = "before") {
        // Check if initialization was successful
        if (!this.canSplit || !this.tableJSON || !this.table) {
            return this._cancel();
        }

        // create a grid with the actual cell coordinates
        const rows = this.table.content.content;
        let colIndex = undefined;
        const grid = [];
        const merged = [];

        for (let rIdx = 0; rIdx < rows.length; rIdx++) {
            const cells = rows[rIdx].content.content;

            // Initialize the grid row
            grid[rIdx] = grid[rIdx] ?? [];
            merged[rIdx] = merged[rIdx] ?? [];

            let cIdx = 0;
            for (let cell of cells) {
                const colspan = cell?.attrs?.colspan;
                const rowspan = cell?.attrs?.rowspan;

                // get the next free column index
                while (grid[rIdx][cIdx]) {
                    cIdx++;
                }

                // Mark the grid cells this cell occupies
                for (let i = 0; i < rowspan; i++) {
                    for (let j = 0; j < colspan; j++) {
                        grid[rIdx + i] = grid[rIdx + i] ?? [];
                        merged[rIdx + i] = merged[rIdx + i] ?? [];

                        grid[rIdx + i][cIdx + j] = cell;
                        merged[rIdx + i][cIdx + j] = i === 0 && j === 0 ? 0 : 1;
                    }
                }

                // to the translation between initial coordinates and real
                // coordinates
                if (cell === this.tableCell) {
                    if (direction === "before" && colIndex === undefined) {
                        colIndex = cIdx;
                    }

                    if (direction === "after") {
                        colIndex = cIdx + colspan;
                    }
                }

                cIdx += colspan;
            }
        }

        // translate the column index to the actual cell id in the table
        const indexes = [];
        for (let i = 0; i < rows.length; i++) {
            const offset = merged[i].slice(0, colIndex).reduce((sum, value) => {
                return sum + value;
            }, 0);

            let index = colIndex - offset;
            if (index < 0) {
                index = 0;
            }

            indexes[i] = index;
        }

        for (let i = 0; i < this.tableJSON.content.length; i++) {
            const row = this.tableJSON.content[i];
            const index = indexes[i];

            const sibling = row.content[index - 1] ?? row.content[index];
            const empty = this.createEmptyTableCell(sibling.type);
            row.content.splice(index, 0, empty);
        }

        // Redistribute column widths to maintain table width
        this._redistributeColumnWidths();

        const newTable = this.editor.schema.nodeFromJSON(this.tableJSON);

        const { from } = this.state.selection;
        this.tr = this.tr.replaceWith(this.tablePos, this.tablePos + this.tableSize, newTable);
        this.tr = this.tr.setSelection(TextSelection.create(this.tr.doc, from));

        return { tr: this.tr, result: true };
    }

    /**
     * Breaks the existing span evenly into multiple cells.
     *
     * @param {*} span - existing span
     * @param {*} numberOfCells - number of cells into which to split
     * @returns {newCellsSpan: number, updatedSpan: number}
     */
    breakSpanEvenly(span, numberOfCells) {
        if (span < numberOfCells) {
            return { newCellsSpan: 1, updatedSpan: 1 };
        }

        const newCellsSpan = Math.floor(span / numberOfCells);
        const updatedSpan = span - newCellsSpan * numberOfCells + newCellsSpan;

        return { newCellsSpan, updatedSpan };
    }

    getCellRow(cell) {
        let rowIndex = -1;

        this.originalTableNode.content.content.forEach((row, i) => {
            row.content.content.forEach((node) => {
                if (node === cell) {
                    rowIndex = i;
                }
            });
        });

        if (rowIndex === -1) {
            return null;
        }

        return this.table.content.content[rowIndex];
    }

    isFootnoteCell(cell) {
        const row = this.getCellRow(cell);

        return row.attrs.isFootnote;
    }

    isLastRowFootnote() {
        return this.originalTableNode.content.content[
            this.originalTableNode.content.content.length - 1
        ].attrs.isFootnote;
    }

    getColumnCount() {
        return this.originalTableNode.content.content[0].content.content.reduce(
            (acc, cell) => acc + cell.attrs.colspan,
            0,
        );
    }
}
